import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ToastContainer, toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import DeleteModal from "../../Components/Common/DeleteModal";
import TableContainer from "../../Components/Common/TableContainer";
import { getTerminslsQuery } from "../../queries";
import "react-toastify/dist/ReactToastify.css";
import {
  createTerminal,
  deleteTerminal,
  editTerminal,
} from "../../api/terminals";
import { useTranslation } from "react-i18next";

const Terminals = () => {
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [terminal, setTerminal] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: terminals } = useQuery({
    ...getTerminslsQuery({
      page,
      containsPosHardwareSerialNumber: searchInput,
    }),
  });



  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: (terminal && terminal.name) || "",
      terminalId: (terminal && terminal.terminalId) || "",
      comment: (terminal && terminal.comment) || "",
      posHardwareSerialNumber:
        (terminal && terminal.posHardwareSerialNumber) || "",
      type: (terminal && terminal.type) || null,
      merchantId: (terminal && terminal.merchantId) || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t("This field is required")),
      terminalId: Yup.string().required(t("This field is required")),
      merchantId: Yup.string().required(t("This field is required")),
      comment: Yup.string().max(255, "Comment can't exceed 255 characters"),
      posHardwareSerialNumber: Yup.string().required(
        t("This field is required")
      ),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const updateterminal = {
          id: terminal.id,
          name: values.name,
          terminalId: values.terminalId,
          comment: values.comment,
          posHardwareSerialNumber: values.posHardwareSerialNumber,
          merchantId: values.merchantId,
        };
        editTerminal(terminal.id, updateterminal)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["terminals"] });
            toast.success(t("Terminal updated successfully"));
          })
          .catch((err) => {
            if (err?.data.error?.message) {
              alert(err?.data.error?.message);
            }
          });
        validation.resetForm();
      } else {
        const newterminal = {
          name: values["name"],
          terminalId: values["terminalId"],
          comment: values["comment"],
          posHardwareSerialNumber: values["posHardwareSerialNumber"],
          merchantId: values["merchantId"],
        };
        createTerminal(newterminal)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["terminals"] });
            toast.success(t("Terminal created successfully"));
          })
          .catch((err) => {
            if (err?.data.error?.message) {
              alert(err?.data.error?.message);
            }
          });
        validation.resetForm();
      }

      closeModal();
    },
  });

  const closeModal = () => {
    setModal(false);
    setTerminal(null);
    validation.resetForm();
  };

  const onDelete = () => {
    deleteTerminal(deleteModal)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["terminals"] });
        toast.success(t("Terminal deleted successfully"));
      })
      .finally(() => {
        setDeleteModal(false);
      });
  };

  const handleEdit = (terminal) => {
    setTerminal(terminal);
    setIsEdit(true);
    setModal(true);
  };

  const columns = useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: t("Type"),
        accessorKey: "type",
        enableColumnFilter: false,
      },
      {
        header: t("Serial number"),
        accessorKey: "posHardwareSerialNumber",
        enableColumnFilter: false,
      },
      {
        header: t("Comment"),
        accessorKey: "comment",
        enableColumnFilter: false,
      },
      {
        header: t("Actions"),
        cell: ({ row }) => (
          <>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => handleEdit(row.original)}
            >
              <i className="ri-edit-fill"></i>
            </button>
            <button
              className="btn btn-sm btn-danger"
              style={{ marginLeft: "10px" }}
              onClick={() => setDeleteModal(row.original.id)}
            >
              <i className="ri-delete-bin-6-fill"></i>
            </button>
          </>
        ),
      },
    ],
    [t]
  );

  document.title = "Terminals | Bazaar";

  return (
    <div className="page-content">
      <DeleteModal
        show={deleteModal}
        onDeleteClick={onDelete}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card id="terminalList">
              <CardHeader className="border-0 ">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="mb-0 fw-bold">{t("Terminals")}</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-success add-btn"
                        id="create-btn"
                        onClick={() => {
                          setTerminal(null);
                          setIsEdit(false);
                          setModal(true);
                          validation.resetForm();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>{" "}
                        {t("create")}
                      </button>
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <div className="py-3 px-3 mb-3 border border-dashed border-end-0 border-start-0">
                <Input
                  type="text"
                  placeholder={t("Serial number")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <div className="card-body pt-0">
                <div>
                  <TableContainer
                    pagination={{
                      setPage: setPage,
                      page: page,
                      total: terminals?.pagination.TotalPages,
                    }}
                    columns={columns}
                    data={terminals?.data?.data || []}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-1"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light text-muted"
                  />
                </div>

                <Modal id="showModal" isOpen={modal} toggle={closeModal} centered>
                  <ModalHeader className="bg-light p-3" toggle={closeModal}>
                    {!!isEdit ? t("edit") : t("create")}
                  </ModalHeader>
                  <Form
                    className="tablelist-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <ModalBody>
                      <input type="hidden" id="id-field" />

                      <div
                        className="mb-3"
                        id="modal-id"
                        style={{ display: "none" }}
                      >
                        <Label htmlFor="id-field1" className="form-label">
                          ID
                        </Label>
                        <Input
                          type="text"
                          id="id-field1"
                          className="form-control"
                          placeholder="ID"
                          readOnly
                        />
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="name-field" className="form-label">
                          {t("Name")}
                        </Label>
                        <Input
                          name="name"
                          id="name-field"
                          className="form-control"
                          placeholder={t("Name")}
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.name || ""}
                          invalid={
                            validation.touched.name && validation.errors.name
                              ? true
                              : false
                          }
                        />
                        {validation.touched.name && validation.errors.name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.name}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label
                          htmlFor="terminalId-field"
                          className="form-label"
                        >
                          {t("Terminal ID")}
                        </Label>
                        <Input
                          name="terminalId"
                          id="terminalId-field"
                          className="form-control"
                          placeholder={t("Terminal ID")}
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.terminalId || ""}
                          invalid={
                            validation.touched.terminalId &&
                              validation.errors.terminalId
                              ? true
                              : false
                          }
                        />
                        {validation.touched.terminalId &&
                          validation.errors.terminalId ? (
                          <FormFeedback type="invalid">
                            {validation.errors.terminalId}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label
                          htmlFor="merchantId-field"
                          className="form-label"
                        >
                          {t("Merchant ID")}
                        </Label>
                        <Input
                          name="merchantId"
                          id="merchantId-field"
                          className="form-control"
                          placeholder={t("Merchant ID")}
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.merchantId || ""}
                          invalid={
                            validation.touched.merchantId &&
                              validation.errors.merchantId
                              ? true
                              : false
                          }
                        />
                        {validation.touched.merchantId &&
                          validation.errors.merchantId ? (
                          <FormFeedback type="invalid">
                            {validation.errors.merchantId}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label
                          htmlFor="posHardwareSerialNumber-field"
                          className="form-label"
                        >
                          {t("Pos hardware serial number")}
                        </Label>
                        <Input
                          name="posHardwareSerialNumber"
                          id="posHardwareSerialNumber-field"
                          className="form-control"
                          placeholder={t("Pos hardware serial number")}
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={
                            validation.values.posHardwareSerialNumber || ""
                          }
                          invalid={
                            validation.touched.posHardwareSerialNumber &&
                              validation.errors.posHardwareSerialNumber
                              ? true
                              : false
                          }
                        />
                        {validation.touched.posHardwareSerialNumber &&
                          validation.errors.posHardwareSerialNumber ? (
                          <FormFeedback type="invalid">
                            {validation.errors.posHardwareSerialNumber}
                          </FormFeedback>
                        ) : null}
                      </div>



                      <div className="mb-3">
                        <Label htmlFor="comment-field" className="form-label">
                          {t("Comment")}
                        </Label>
                        <Input
                          name="comment"
                          id="comment-field"
                          className="form-control"
                          placeholder={t("Comment")}
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.comment || ""}
                          invalid={
                            validation.touched.comment &&
                              validation.errors.comment
                              ? true
                              : false
                          }
                        />
                        {validation.touched.comment &&
                          validation.errors.comment ? (
                          <FormFeedback type="invalid">
                            {validation.errors.comment}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <div className="hstack gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => {
                            closeModal();
                          }}
                        >
                          {t("Close")}
                        </button>

                        <button type="submit" className="btn btn-success">
                          {!!isEdit ? t("Update") : t("create")}
                        </button>
                      </div>
                    </ModalFooter>
                  </Form>
                </Modal>
                <ToastContainer closeButton={false} limit={1} />
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Terminals;
